import { getCookies } from '../hooks/useCookies';

const PERMISSION_KEYS = ['create', 'edit', 'view', 'delete'];

/** Routes every authenticated admin may open (not tied to sidebar menu selection). */
const ALWAYS_ALLOWED_PATH_PREFIXES = [
  '/profile',
  '/profile-edit',
  '/settings',
  '/activity-logs',
  '/preferences',
  '/changePassword',
];

const isSubAdmin = () =>
  String(getCookies('USERROLE') || '')
    .toLowerCase()
    .replace(/\s+/g, '-') === 'sub-admin';

/** Baseline sub-admin routes (aligned with API defaultroles merge). */
const SUB_ADMIN_BASELINE_PATH_PREFIXES = ['/dashboard'];

/** Map admin routes to permission section ids (aligned with API menu modules). */
const ROUTE_SECTION_PREFIXES = [
  { prefix: '/dashboard', section: 'dashboard' },
  { prefix: '/user', section: 'user' },
  { prefix: '/sub-admins', section: 'sub-admins' },
  { prefix: '/safty-alerts', section: 'safty-alerts' },
  { prefix: '/reports-management', section: 'reports-management' },
  { prefix: '/poi-management', section: 'poi-management' },
  { prefix: '/blocked-users', section: 'blocked-users' },
  { prefix: '/sections', section: 'sections' },
  { prefix: '/employee', section: 'employee' },
  { prefix: '/movies', section: 'movies' },
  { prefix: '/contact-messages', section: 'contact-messages' },
  { prefix: '/feedbacks', section: 'feedbacks' },
  { prefix: '/sea-route-reports', section: 'sea-route-reports' },
  { prefix: '/roles', section: 'roles' },
  { prefix: '/activity-logs', section: 'activity-logs' },
  { prefix: '/notifications', section: 'notifications' },
  { prefix: '/settings', section: 'settings' },
];

const pathMatchesMenu = (pathname, urlPath) => {
  if (!urlPath) return false;
  return pathname === urlPath || pathname.startsWith(`${urlPath}/`);
};

export const getUserPermissions = () => {
  const raw = getCookies('USERPERMISSION');
  return Array.isArray(raw) ? raw : [];
};

export const hasSectionAccess = (permissions, section) => {
  if (!section || !Array.isArray(permissions)) return false;

  const entry = permissions.find((item) => item?.section === section);
  if (!entry) return false;

  if (Array.isArray(entry.submenu) && entry.submenu.length) {
    return entry.submenu.some((sub) => PERMISSION_KEYS.some((key) => sub[key]));
  }

  return PERMISSION_KEYS.some((key) => entry[key]);
};

export const getSectionForPath = (pathname) => {
  const match = ROUTE_SECTION_PREFIXES.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return match?.section;
};

const isAlwaysAllowedPath = (pathname) =>
  ALWAYS_ALLOWED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

const isSubAdminBaselinePath = (pathname) =>
  isSubAdmin() &&
  SUB_ADMIN_BASELINE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

const isPathInMenus = (pathname, menus) =>
  menus.some((item) => {
    if (pathMatchesMenu(pathname, item?.urlPath)) return true;
    if (Array.isArray(item?.subMenu)) {
      return item.subMenu.some((sub) => pathMatchesMenu(pathname, sub?.urlPath));
    }
    return false;
  });

/** Whether the user may open this admin route (menus + cookie permissions). */
export const isPathAllowedForUser = (pathname, menus = []) => {
  if (isAlwaysAllowedPath(pathname) || isSubAdminBaselinePath(pathname)) {
    return true;
  }

  if (Array.isArray(menus) && menus.length > 0) {
    return isPathInMenus(pathname, menus);
  }

  const section = getSectionForPath(pathname);
  if (!section) {
    return true;
  }

  return hasSectionAccess(getUserPermissions(), section);
};

export const getDefaultAdminHomePath = (menus = []) => {
  if (!Array.isArray(menus) || menus.length === 0) {
    return '/dashboard';
  }

  const dashboardItem = menus.find(
    (item) => item?.urlPath === '/dashboard' || item?.modules === 'dashboard',
  );
  if (dashboardItem?.urlPath) {
    return dashboardItem.urlPath;
  }

  const first = menus[0];
  if (first?.subMenu?.length) {
    return first.subMenu[0]?.urlPath || '/dashboard';
  }

  return first?.urlPath || '/dashboard';
};
